import { Badge, Checkbox, Icon } from "@elaniintc/goes-design-system";
import { useState } from "react";
import { cn } from "@/utils/theme";

interface Props {
  isLoading: boolean;
  selectedItems: string[];
  handleCheckboxChange: (id: string) => void;
  items: Record<
    string,
    {
      id: string;
      name: string;
      display_name: string;
      group_name: string;
    }[]
  >;
  canEdit?: boolean;
}

const PermissionsList = ({
  items,
  isLoading,
  selectedItems,
  handleCheckboxChange,
  canEdit = true,
}: Props) => {
  if (isLoading) {
    return (
      <div className="h-screen w-full">
        <div className="bg-neutral-object border-y border-neutral w-full h-2" />
        <div className="w-full h-full flex justify-center items-center">
          <Icon
            type="far"
            name="spinner-third"
            className="animate-spin text-on-disabled text-xl"
            aria-hidden="true"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto">
      {Object.keys(items).map((key) => {
        const list = items[key];
        return (
          <ListItem
            key={`permission-list-item-${key}`}
            name={key}
            permissions={list}
            handleCheckboxChange={handleCheckboxChange}
            selectedItems={selectedItems}
            canEdit={canEdit}
          />
        );
      })}
    </div>
  );
};

export default PermissionsList;

const ListItem = ({
  name,
  permissions,
  selectedItems,
  canEdit = true,
  handleCheckboxChange,
}: {
  name: string;
  permissions: Props["items"][string];
  selectedItems: string[];
  canEdit?: boolean;
  handleCheckboxChange: (id: string) => void;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const permissionGroup = new Set(
    permissions.map(({ name }) => {
      return name.split(":")[0];
    }),
  );
  return (
    <div>
      <div className="bg-neutral-object z-30 sticky top-0">
        <button
          className={cn(
            "w-full flex items-center justify-between z-30",
            "px-3 py-2.5 bg-neutral-object-accent-silent",
            "text-body-loud font-semibold text-xs border-neutral uppercase",
            isCollapsed ? "border-t" : "border-y",
          )}
          type="button"
          onClick={() => {
            setIsCollapsed(!isCollapsed);
          }}
        >
          <span>{name}</span>
          <div className="flex items-center gap-2">
            <Badge size="xs" className="font-mono lowercase rounded-lg">
              {Array.from(permissionGroup).join("/")}
            </Badge>
            <Icon type="far" name="angle-down" />
          </div>
        </button>
      </div>
      <div
        className={cn(
          "grid",
          isCollapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
          "transition-[grid-template-rows] duration-300 ease-in-out",
        )}
      >
        <div className="overflow-hidden bg-neutral-object">
          {permissions.map((permission, index) => {
            const isChecked = selectedItems.includes(permission.id);
            return (
              <div
                key={`permission-${permission}-${index}`}
                className="px-3 py-2.5 text-body font-normal text-xs flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Checkbox
                    className="w-5 h-5 focus:ring-1!"
                    id={permission.name}
                    name={permission.name}
                    checked={isChecked}
                    disabled={!canEdit}
                    onChange={() => handleCheckboxChange(permission.id)}
                    {...(isCollapsed && {
                      "aria-hidden": true,
                      tabIndex: -1,
                    })}
                  />
                  <label
                    htmlFor={permission.name}
                    className="text-body shrink-0"
                  >
                    {permission.display_name}
                  </label>
                </div>
                <Badge size="xs" className="font-mono rounded-lg">
                  {permission.name.split(":")[1]}
                </Badge>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
